import React, { useMemo, useEffect, useState, useCallback } from "react";
import {
  Box,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
  Tooltip,
  Chip,
  InputAdornment,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Button,
  Autocomplete,
} from "@mui/material";
import {
  Print as PrintIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  Visibility,
  VisibilityOff,
  CalendarToday,
  CheckCircle,
} from "@mui/icons-material";
import {
  getCompleteBillings,
  billUpdate,
  partyList,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";
import { printBillReport } from "../Bill/billPDFGenerator";
import { useDebounce } from "../../../Hooks/useDebounce";
import { T } from "../../../theme";

const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryBg,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};
const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.primaryBg },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

const inlineFieldSx = {
  "& .MuiInputBase-root": { ...sq, bgcolor: T.primaryBg, fontSize: "0.68rem" },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);
const getInitials = (s) =>
  s
    ? s
        .split(" ")
        .filter((w) => w)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "";
const getRefNo = (r) =>
  `${r.branch_name === "RAIPUR" ? "SRE" : r.branch_name === "BILASPUR" ? "JHT" : "PRL"}/${r.billing_id}/${getInitials(r.party_name)}`;

// ── ActionButton ───────────────────────────────────────────────────
const ActionButton = ({
  tip,
  icon,
  color,
  isEdit,
  isPrint,
  isSave,
  isCancel,
  onClick,
}) => {
  const c = isEdit
    ? "#757575"
    : isPrint
      ? T.warning
      : isSave
        ? T.success
        : isCancel
          ? T.red
          : color || T.primary;

  const bg = isEdit
    ? "#f5f5f5"
    : isPrint
      ? T.warningBg
      : isSave
        ? T.successBg
        : isCancel
          ? T.redBg
          : `${c}14`;

  const bd = isEdit
    ? "#e0e0e0"
    : isPrint
      ? `${T.warning}30`
      : isSave
        ? `${T.success}30`
        : isCancel
          ? T.redBorder
          : `${c}30`;

  const hBg = isEdit
    ? "#eeeeee"
    : isPrint
      ? "#fef3c7"
      : isSave
        ? "#d1fae5"
        : isCancel
          ? "#fee2e2"
          : `${c}28`;

  return (
    <Tooltip title={tip} arrow>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 20,
          height: 20,
          ...sq,
          bgcolor: bg,
          color: c,
          border: `1px solid ${bd}`,
          transition: "all 0.18s",
          "&:hover": {
            bgcolor: hBg,
            transform: "translateY(-1px)",
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 12 } })}
      </IconButton>
    </Tooltip>
  );
};

// ── StatCard ───────────────────────────────────────────────────────
const StatCard = ({ title, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1, sm: 1.5 },
      textAlign: "center",
      ...sq,
      border: `1.5px solid ${color}40`,
      borderTop: `3px solid ${color}`,
      bgcolor: `${color}08`,
      boxShadow: "none",
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: "0.55rem", sm: "0.65rem" },
        color: T.textSub,
        lineHeight: 1.2,
        mb: 0.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: "0.72rem", sm: "1rem" },
        fontWeight: 700,
        color,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "block",
      }}
      title={value}
    >
      {value}
    </Typography>
  </Paper>
);

// ── LabelValue ─────────────────────────────────────────────────────
const LabelValue = ({ label, value, color }) => (
  <Box>
    <Typography
      sx={{
        fontSize: "0.58rem",
        color: T.textMuted,
        fontWeight: 600,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: "0.7rem", fontWeight: 600, color: color || T.text }}
    >
      {value}
    </Typography>
  </Box>
);

// ── MobileBillCard ─────────────────────────────────────────────────
const MobileBillCard = ({ row, idx, expanded, onToggle, onEdit, onPrint }) => {
  const isNegative = row.rem_amount < 0;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        border: `1.5px solid ${expanded ? T.primary : T.border}`,
        bgcolor: T.white,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
      }}
    >
      {/* Card Header */}
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        {/* Icon column */}
        <Box
          sx={{
            width: 58,
            flexShrink: 0,
            bgcolor: expanded ? T.success : `${T.success}14`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.success : T.border}`,
            transition: "background-color 0.2s",
            gap: 0.25,
          }}
        >
          <CheckCircle
            sx={{ fontSize: 20, color: expanded ? T.white : T.success }}
          />
          <Typography
            sx={{
              fontSize: "0.46rem",
              fontWeight: 700,
              color: expanded ? T.white : T.primary,
            }}
          >
            #{idx + 1}
          </Typography>
        </Box>

        {/* Main info */}
        <Box
          sx={{
            flex: 1,
            px: 1.2,
            py: 0.75,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.78rem",
              color: T.text,
              lineHeight: 1.2,
              mb: 0.15,
            }}
          >
            {getRefNo(row)}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.62rem",
              color: T.textSub,
              fontWeight: 500,
              mb: 0.15,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.party_name || "—"}
          </Typography>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}
          >
            <CalendarToday sx={{ fontSize: 10, color: T.textMuted }} />
            <Typography sx={{ fontSize: "0.58rem", color: T.textMuted }}>
              {formatDate(row.created_at)}
            </Typography>
          </Box>
          <Chip
            label="Completed"
            size="small"
            icon={<CheckCircle sx={{ fontSize: 10 }} />}
            sx={{
              ...sq,
              alignSelf: "flex-start",
              bgcolor: `${T.success}14`,
              color: T.success,
              fontSize: "0.48rem",
              height: 15,
              border: `1px solid ${T.success}30`,
              "& .MuiChip-icon": { color: T.success, ml: 0.4 },
            }}
          />
        </Box>

        {/* Amount + toggle */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            px: 1,
            py: 0.75,
            minWidth: 100,
          }}
        >
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontSize: "0.48rem", color: T.textMuted, fontWeight: 600 }}
            >
              Balance
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.8rem",
                color: isNegative ? T.red : T.success,
              }}
            >
              {formatCurrency(row.rem_amount)}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 22,
              fontSize: "0.55rem",
              px: 0.7,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "View"}
          </Button>
        </Box>
      </Box>

      {/* Expanded detail */}
      {expanded && (
        <Box
          sx={{
            bgcolor: T.primaryBg,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.25,
          }}
        >
          <Stack spacing={0.75}>
            {/* Amounts */}
            <Box
              sx={{
                p: 0.75,
                bgcolor: T.white,
                border: `1px solid ${T.border}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.4,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                💳 Amounts
              </Typography>
              <Grid container spacing={0.5}>
                {[
                  ["Total", formatCurrency(row.total_amount), null, 6],
                  [
                    `TDS ${row.tds_percent ? `(${row.tds_percent}%)` : ""}`,
                    formatCurrency(row.tds_amount),
                    T.red,
                    6,
                  ],
                  ["Deduction", formatCurrency(row.deduction_amount), T.red, 6],
                  [
                    "Net Payable",
                    formatCurrency(row.net_payable),
                    T.primary,
                    6,
                  ],
                  ["Paid", formatCurrency(row.paid_amount), T.success, 6],
                  [
                    "Balance",
                    formatCurrency(row.rem_amount),
                    isNegative ? T.red : T.success,
                    6,
                  ],
                ].map(([l, v, c, xs]) => (
                  <Grid item xs={xs} key={l}>
                    <LabelValue label={l} value={v} color={c} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Remarks */}
            {(row.remarks || row.payment_remarks) && (
              <Box
                sx={{
                  p: 0.75,
                  bgcolor: T.white,
                  border: `1px solid ${T.border}`,
                  ...sq,
                }}
              >
                {row.remarks && (
                  <LabelValue label="Remark" value={row.remarks} />
                )}
                {row.payment_remarks && (
                  <Box sx={{ mt: 0.5 }}>
                    <LabelValue
                      label="Pay Remark"
                      value={row.payment_remarks}
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                pt: 0.75,
                borderTop: `1px dashed ${T.border}`,
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <Button
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 13 }} />}
                onClick={() => onEdit(row)}
                sx={{
                  ...sq,
                  bgcolor: `${T.primary}14`,
                  color: T.primary,
                  fontSize: "0.65rem",
                  textTransform: "none",
                  px: 1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: `${T.primary}28`, boxShadow: "none" },
                }}
              >
                Edit
              </Button>
              <Button
                size="small"
                startIcon={<PrintIcon sx={{ fontSize: 13 }} />}
                onClick={() => onPrint(row)}
                sx={{
                  ...sq,
                  bgcolor: `${T.warning}14`,
                  color: T.warning,
                  fontSize: "0.65rem",
                  textTransform: "none",
                  px: 1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: `${T.warning}28`, boxShadow: "none" },
                }}
              >
                Print
              </Button>
            </Box>
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

// ── Main Component ─────────────────────────────────────────────────
export default function Completebill() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [billData, setBillData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [partyOptions, setParties] = useState([]);
  const [editableRow, setEditableRow] = useState(null);
  const [editData, setEditData] = useState({
    follow_date: "",
    remarks: "",
    payment_remarks: "",
  });
  const [filters, setFilters] = useState({
    lrNumber: "",
    payDate: "",
    party: "",
  });

  const debouncedLrNumber = useDebounce(filters.lrNumber, 500);
  const debouncedPayDate = useDebounce(filters.payDate, 500);
  const debouncedParty = useDebounce(filters.party, 500);
  const [expandedCardId, setExpandedCardId] = useState(null);

  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  useEffect(() => {
    const fetchParties = async () => {
      try {
        const res = await partyList({ party_type: "consignor" });
        setParties(Array.isArray(res) ? res : []);
      } catch (error) {
        console.error("Failed to fetch parties:", error);
        setParties([]);
      }
    };
    fetchParties();
  }, []);

  const fetchBillings = useCallback(
    async (pn, af = filters) => {
      if (loading) return;
      setLoading(true);
      try {
        const d = await getCompleteBillings(pn, af);
        if (pn === 1) {
          setBillData(d.results || []);
          setPage(2);
        } else if (d?.results?.length) {
          setBillData((p) => [...p, ...d.results]);
          setPage(pn + 1);
        }
        setHasMore(Boolean(d?.next));
      } catch {
      } finally {
        setLoading(false);
      }
    },
    [filters, loading],
  );

  useEffect(() => {
    const activeFilters = {
      lrNumber: debouncedLrNumber,
      payDate: debouncedPayDate,
      party: debouncedParty,
    };
    setPage(1);
    fetchBillings(1, activeFilters);
    setExpandedCardId(null);
  }, [debouncedLrNumber, debouncedPayDate, debouncedParty]);

  const fetchMoreData = () => {
    if (!loading && hasMore)
      fetchBillings(page, {
        lrNumber: debouncedLrNumber,
        payDate: debouncedPayDate,
        party: debouncedParty,
      });
  };

  const handleFilterChange = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const clearFilters = () =>
    setFilters({ lrNumber: "", payDate: "", party: "" });

  const filteredData = useMemo(
    () =>
      billData.filter((r) => {
        const ml = debouncedLrNumber
          ? r.freights?.some((f) =>
              f.lr_number
                ?.toString()
                .toLowerCase()
                .includes(debouncedLrNumber.toLowerCase()),
            )
          : true;
        const mp = debouncedParty
          ? r.party_name?.toLowerCase().includes(debouncedParty.toLowerCase())
          : true;
        return ml && mp;
      }),
    [debouncedLrNumber, debouncedParty, billData],
  );

  const handleEditSave = async (r) => {
    try {
      const d = await billUpdate(r.billing_id, {
        follow_date: editData.follow_date || null,
        remarks: editData.remarks,
        payment_remarks: editData.payment_remarks,
      });
      setBillData((p) => p.map((x) => (x.billing_id === d.billing_id ? d : x)));
      setEditableRow(null);
    } catch {}
  };

  const handlePrint = (row) =>
    printBillReport([row], {
      total_amount: row.total_amount,
      paid_amount: row.paid_amount,
      rem_amount: row.rem_amount,
    });

  const totals = {
    total_amount: filteredData.reduce((s, c) => s + (+c.total_amount || 0), 0),
    tds_amount: filteredData.reduce((s, c) => s + (+c.tds_amount || 0), 0),
    deduction_amount: filteredData.reduce(
      (s, c) => s + (+c.deduction_amount || 0),
      0,
    ),
    net_payable: filteredData.reduce((s, c) => s + (+c.net_payable || 0), 0),
    paid_amount: filteredData.reduce((s, c) => s + (+c.paid_amount || 0), 0),
    rem_amount: filteredData.reduce((s, c) => s + (+c.rem_amount || 0), 0),
  };

  const EmptyState = () => (
    <Box
      sx={{
        textAlign: "center",
        py: 8,
        border: `1.5px dashed ${T.border}`,
        bgcolor: T.primaryBg,
        ...sq,
      }}
    >
      <CheckCircle sx={{ fontSize: 44, color: `${T.success}50`, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: T.text, fontSize: "0.9rem" }}>
        No completed bills
      </Typography>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -4, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Stat Cards ── */}
      <Box sx={{ mt: 2, mb: 1.5, width: "100%" }}>
        <Grid
          container
          spacing={0.75}
          sx={{
            mx: 0,
            width: "100%",
            "& > .MuiGrid-item": { px: "3px !important", py: "3px !important" },
          }}
        >
          {[
            {
              title: "Total Amount",
              value: formatCurrency(totals.total_amount),
              color: T.primary,
            },
            {
              title: "Net Payable",
              value: formatCurrency(totals.net_payable),
              color: T.warning,
            },
            {
              title: "Paid Amount",
              value: formatCurrency(totals.paid_amount),
              color: T.success,
            },
            {
              title: "Remaining",
              value: formatCurrency(totals.rem_amount),
              color: T.red,
            },
          ].map((s) => (
            <Grid item xs={6} sm={3} key={s.title}>
              <StatCard {...s} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Toolbar ── */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          {/* LR Number */}
          <Grid item xs={12} sm={4} md={2.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="LR No."
              value={filters.lrNumber}
              onChange={(e) => handleFilterChange("lrNumber", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReceiptIcon sx={{ fontSize: 14, color: T.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          {/* Pay Date */}
          <Grid item xs={12} sm={4} md={2.5}>
            <TextField
              fullWidth
              type="date"
              size="small"
              label="Pay Date"
              InputLabelProps={{ shrink: true }}
              value={filters.payDate}
              onChange={(e) => handleFilterChange("payDate", e.target.value)}
              sx={filterInputSx}
            />
          </Grid>

          {/* Party */}
          <Grid item xs={12} sm={4} md={2.5}>
            <Autocomplete
              freeSolo
              options={partyOptions.map((o) => o.name)}
              value={filters.party}
              onInputChange={(_, v) => handleFilterChange("party", v || "")}
              size="small"
              renderInput={(p) => (
                <TextField
                  {...p}
                  placeholder="Party"
                  InputProps={{
                    ...p.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <BusinessIcon
                            sx={{ fontSize: 14, color: T.textMuted }}
                          />
                        </InputAdornment>
                        {p.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* Spacer */}
          <Grid
            item
            xs={false}
            md
            sx={{ display: { xs: "none", md: "block" } }}
          />

          {/* Clear */}
          <Grid item xs="auto">
            <Tooltip title="Clear Filters" arrow>
              <span>
                <Button
                  size="small"
                  onClick={clearFilters}
                  sx={{ ...dangerBtn, height: 34, minWidth: 34, px: 1 }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* ── Mobile View ── */}
      {isMobile ? (
        <Box>
          {loading && !billData.length ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 10,
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: T.primary }} size={32} />
              <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
                Loading…
              </Typography>
            </Box>
          ) : !filteredData.length ? (
            <EmptyState />
          ) : (
            <Box
              id="complete-bill-mobile"
              sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}
            >
              <InfiniteScroll
                dataLength={billData.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <CircularProgress size={20} sx={{ color: T.primary }} />
                  </Box>
                }
                scrollableTarget="complete-bill-mobile"
              >
                {filteredData.map((r, i) => (
                  <MobileBillCard
                    key={r.billing_id}
                    row={r}
                    idx={i}
                    expanded={expandedCardId === r.billing_id}
                    onToggle={() => handleCardToggle(r.billing_id)}
                    onEdit={(row) => {
                      setEditableRow(row.billing_id);
                      setEditData({
                        follow_date: row.follow_date || "",
                        remarks: row.remarks || "",
                        payment_remarks: row.payment_remarks || "",
                      });
                    }}
                    onPrint={handlePrint}
                  />
                ))}
              </InfiniteScroll>

              {/* Mobile Summary Footer */}
              <Paper
                elevation={0}
                sx={{
                  ...sq,
                  border: `1.5px solid ${T.border}`,
                  bgcolor: T.primaryLight,
                  p: 1.5,
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    color: T.primary,
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  📊 Grand Summary
                </Typography>
                <Grid container spacing={0.75}>
                  {[
                    ["Total", formatCurrency(totals.total_amount), T.primary],
                    [
                      "Net Payable",
                      formatCurrency(totals.net_payable),
                      T.warning,
                    ],
                    ["Paid", formatCurrency(totals.paid_amount), T.success],
                    ["Remaining", formatCurrency(totals.rem_amount), T.red],
                  ].map(([l, v, c]) => (
                    <Grid item xs={6} key={l}>
                      <Typography
                        sx={{
                          fontSize: "0.58rem",
                          color: T.textMuted,
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        {l}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.75rem", fontWeight: 700, color: c }}
                      >
                        {v}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
          )}
        </Box>
      ) : (
        /* ── Desktop View ── */
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
          }}
        >
          <Box
            id="complete-bill-container"
            sx={{ maxHeight: "calc(100vh - 380px)", overflow: "auto" }}
          >
            <InfiniteScroll
              dataLength={billData.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="complete-bill-container"
            >
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 1620,
                    borderCollapse: "collapse",
                    "& td, & th": {
                      py: 0,
                      px: 0.75,
                      fontSize: "0.75rem",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                    },
                    "& td": {
                      borderBottom: `1px solid #ccc`,
                      height: 26,
                    },
                    "& th": {
                      fontWeight: 700,
                      color: T.headerText,
                      bgcolor: `${T.headerBg} !important`,
                      py: 0.2,
                      height: 28,
                      fontSize: "0.8rem",
                      letterSpacing: "0.01em",
                      borderBottom: `1px solid #ccc`,
                      lineHeight: 1.2,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {[
                        { l: "#", w: 35, sticky: true, left: 0 },
                        { l: "Ref No", w: 130, sticky: true, left: 35 },
                        { l: "Party", w: 150 },
                        { l: "Bill Date", w: 95 },
                        { l: "Total Amt", w: 100 },
                        { l: "TDS Amt", w: 100 },
                        { l: "Deduction", w: 110 },
                        { l: "Net Payable", w: 110, highlight: true },
                        { l: "Rec Amt", w: 100 },
                        { l: "Bal Amt", w: 100, highlight: true },
                        { l: "Follow Date", w: 110 },
                        { l: "Follow Remark", w: 130 },
                        { l: "Pay Remark", w: 130 },
                        { l: "Actions", w: 100, stickyRight: true },
                      ].map(
                        ({ l, w, sticky, left, highlight, stickyRight }) => (
                          <TableCell
                            key={l}
                            sx={{
                              minWidth: w,
                              bgcolor: `${T.headerBg} !important`,
                              color: highlight
                                ? `${T.success} !important`
                                : `${T.headerText} !important`,
                              ...(sticky && {
                                position: "sticky",
                                left,
                                zIndex: 12,
                              }),
                              ...(stickyRight && {
                                position: "sticky",
                                right: 0,
                                zIndex: 12,
                              }),
                            }}
                          >
                            {l}
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading && !billData.length ? (
                      <TableRow>
                        <TableCell
                          colSpan={14}
                          sx={{ textAlign: "center", py: 6, border: "none" }}
                        >
                          <CircularProgress
                            sx={{ color: T.primary }}
                            size={32}
                          />
                        </TableCell>
                      </TableRow>
                    ) : !filteredData.length ? (
                      <TableRow>
                        <TableCell
                          colSpan={14}
                          sx={{ textAlign: "center", py: 6, border: "none" }}
                        >
                          <CheckCircle
                            sx={{
                              fontSize: 40,
                              color: `${T.success}50`,
                              mb: 1,
                              display: "block",
                              mx: "auto",
                            }}
                          />
                          <Typography
                            sx={{
                              color: T.textSub,
                              fontSize: "0.8rem",
                              fontWeight: 600,
                            }}
                          >
                            No completed bills found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {filteredData.map((row, idx) => {
                          const isEditing = editableRow === row.billing_id;
                          const isNegative = row.rem_amount < 0;
                          const isEven = idx % 2 === 0;
                          const rowBg = isEven ? T.baseRow : T.altRow;
                          const sc = {
                            bgcolor: `${rowBg} !important`,
                            zIndex: 5,
                          };

                          return (
                            <TableRow
                              key={row.billing_id}
                              sx={{
                                "& > td": {
                                  bgcolor: rowBg,
                                  transition: "background-color 0.12s",
                                },
                                "&:hover > td": {
                                  bgcolor: `${T.rowHover} !important`,
                                },
                              }}
                            >
                              {/* # */}
                              <TableCell
                                sx={{ position: "sticky", left: 0, ...sc }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    color: T.textMuted,
                                  }}
                                >
                                  {idx + 1}
                                </Typography>
                              </TableCell>

                              {/* Ref No */}
                              <TableCell
                                sx={{ position: "sticky", left: 35, ...sc }}
                              >
                                <Chip
                                  label={getRefNo(row)}
                                  size="small"
                                  sx={{
                                    ...sq,
                                    bgcolor: T.primaryBg,
                                    color: T.primary,
                                    fontSize: "0.6rem",
                                    height: 16,
                                    border: `1px solid ${T.border}`,
                                    fontWeight: 700,
                                    "& .MuiChip-label": { px: 0.75 },
                                  }}
                                />
                              </TableCell>

                              {/* Party */}
                              <TableCell
                                sx={{
                                  maxWidth: 150,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {row.party_name}
                              </TableCell>

                              {/* Bill Date */}
                              <TableCell sx={{ fontSize: "0.75rem" }}>
                                {formatDate(row.created_at)}
                              </TableCell>

                              {/* Total Amt */}
                              <TableCell
                                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                              >
                                {formatCurrency(row.total_amount)}
                              </TableCell>

                              {/* TDS Amt — NEW */}
                              <TableCell>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    color: T.red,
                                  }}
                                >
                                  {formatCurrency(row.tds_amount)}
                                </Typography>
                              </TableCell>

                              {/* Deduction Amt — NEW */}
                              <TableCell>
                                <Tooltip
                                  title={
                                    row.deduction_remarks ||
                                    "No deduction remarks"
                                  }
                                  arrow
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                      color: T.red,
                                    }}
                                  >
                                    {formatCurrency(row.deduction_amount)}
                                  </Typography>
                                </Tooltip>
                              </TableCell>

                              {/* Net Payable — NEW (highlighted) */}
                              <TableCell>
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: "0.78rem",
                                    color: T.primary,
                                  }}
                                >
                                  {formatCurrency(row.net_payable)}
                                </Typography>
                              </TableCell>

                              {/* Rec Amt */}
                              <TableCell
                                sx={{
                                  color: T.success,
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {formatCurrency(row.paid_amount)}
                              </TableCell>

                              {/* Bal Amt */}
                              <TableCell>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                    color: isNegative ? T.red : T.success,
                                  }}
                                >
                                  {formatCurrency(row.rem_amount)}
                                </Typography>
                              </TableCell>

                              {/* Follow Date — editable */}
                              <TableCell>
                                {isEditing ? (
                                  <TextField
                                    type="date"
                                    size="small"
                                    value={editData.follow_date}
                                    onChange={(e) =>
                                      setEditData((p) => ({
                                        ...p,
                                        follow_date: e.target.value,
                                      }))
                                    }
                                    sx={{ width: 110, ...inlineFieldSx }}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                ) : (
                                  <Typography sx={{ fontSize: "0.75rem" }}>
                                    {row.follow_date
                                      ? formatDate(row.follow_date)
                                      : "—"}
                                  </Typography>
                                )}
                              </TableCell>

                              {/* Follow Remark — editable */}
                              <TableCell>
                                {isEditing ? (
                                  <TextField
                                    size="small"
                                    value={editData.remarks}
                                    onChange={(e) =>
                                      setEditData((p) => ({
                                        ...p,
                                        remarks: e.target.value,
                                      }))
                                    }
                                    sx={{ width: 120, ...inlineFieldSx }}
                                  />
                                ) : (
                                  <Tooltip title={row.remarks || ""} arrow>
                                    <Typography
                                      sx={{
                                        maxWidth: 120,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      {row.remarks || "—"}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </TableCell>

                              {/* Pay Remark — editable */}
                              <TableCell>
                                {isEditing ? (
                                  <TextField
                                    size="small"
                                    value={editData.payment_remarks}
                                    onChange={(e) =>
                                      setEditData((p) => ({
                                        ...p,
                                        payment_remarks: e.target.value,
                                      }))
                                    }
                                    sx={{ width: 120, ...inlineFieldSx }}
                                  />
                                ) : (
                                  <Tooltip
                                    title={row.payment_remarks || ""}
                                    arrow
                                  >
                                    <Typography
                                      sx={{
                                        maxWidth: 120,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      {row.payment_remarks || "—"}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </TableCell>

                              {/* Actions */}
                              <TableCell
                                sx={{
                                  position: "sticky",
                                  right: 0,
                                  ...sc,
                                  py: "2px !important",
                                }}
                              >
                                {isEditing ? (
                                  <Stack
                                    direction="row"
                                    spacing={0.5}
                                    justifyContent="center"
                                  >
                                    <ActionButton
                                      tip="Save"
                                      icon={<SaveIcon />}
                                      isSave
                                      onClick={() => handleEditSave(row)}
                                    />
                                    <ActionButton
                                      tip="Cancel"
                                      icon={<CloseIcon />}
                                      isCancel
                                      onClick={() => setEditableRow(null)}
                                    />
                                  </Stack>
                                ) : (
                                  <Stack
                                    direction="row"
                                    spacing={0.5}
                                    justifyContent="center"
                                  >
                                    <ActionButton
                                      tip="Edit"
                                      icon={<EditIcon />}
                                      isEdit
                                      onClick={() => {
                                        setEditableRow(row.billing_id);
                                        setEditData({
                                          follow_date: row.follow_date || "",
                                          remarks: row.remarks || "",
                                          payment_remarks:
                                            row.payment_remarks || "",
                                        });
                                      }}
                                    />
                                    <ActionButton
                                      tip="Print"
                                      icon={<PrintIcon />}
                                      isPrint
                                      onClick={() => handlePrint(row)}
                                    />
                                  </Stack>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}

                        {/* Totals Row */}
                        <TableRow
                          sx={{
                            position: "sticky",
                            bottom: 0,
                            zIndex: 20,
                            "& td": {
                              bgcolor: T.successBg,
                              fontWeight: 600,
                              fontSize: "0.68rem",
                              py: 0.5,
                              borderTop: `2px solid ${T.primary}`,
                              borderBottom: "none",
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              zIndex: 22,
                              bgcolor: `${T.successBg} !important`,
                            }}
                          >
                            —
                          </TableCell>
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 35,
                              zIndex: 22,
                              color: T.primary,
                              fontWeight: 800,
                              bgcolor: `${T.successBg} !important`,
                            }}
                          >
                            Totals
                          </TableCell>
                          <TableCell colSpan={2} /> {/* Party, Bill Date */}
                          <TableCell sx={{ fontWeight: 700 }}>
                            {formatCurrency(totals.total_amount)}
                          </TableCell>
                          <TableCell sx={{ color: T.red, fontWeight: 700 }}>
                            {formatCurrency(totals.tds_amount)}
                          </TableCell>
                          <TableCell sx={{ color: T.red, fontWeight: 700 }}>
                            {formatCurrency(totals.deduction_amount)}
                          </TableCell>
                          <TableCell sx={{ color: T.primary, fontWeight: 800 }}>
                            {formatCurrency(totals.net_payable)}
                          </TableCell>
                          <TableCell sx={{ color: T.success, fontWeight: 700 }}>
                            {formatCurrency(totals.paid_amount)}
                          </TableCell>
                          <TableCell sx={{ color: T.success, fontWeight: 800 }}>
                            {formatCurrency(totals.rem_amount)}
                          </TableCell>
                          <TableCell colSpan={3} />{" "}
                          {/* Follow Date, Follow Remark, Pay Remark */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              zIndex: 22,
                              bgcolor: `${T.successBg} !important`,
                            }}
                          />
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </InfiniteScroll>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
